// ============================================================================
// SAMPLE SERVICE SERVICE (Reference Implementation)
// ============================================================================
// API calls for Sample Service Master module

import { get, post, put, del } from '../client/axiosClient';
import { sampleServiceEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
  SampleServiceDto,
  CreateSampleServiceRequest,
  UpdateSampleServiceRequest,
  SampleServiceQueryParams,
} from '../types';

class SampleServiceService {
  /** Get paginated list of services */
  async getAll(params?: SampleServiceQueryParams): Promise<PaginatedResponse<SampleServiceDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(sampleServiceEndpoints.list(), params)
      : sampleServiceEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<SampleServiceDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  /** Get single service by ID */
  async getById(id: ID): Promise<ApiResponse<SampleServiceDto>> {
    const response = await get<ApiResponse<SampleServiceDto>>(
      sampleServiceEndpoints.getById(id)
    );
    return response.data;
  }

  /** Create new service */
  async create(data: CreateSampleServiceRequest): Promise<ApiResponse<SampleServiceDto>> {
    const response = await post<ApiResponse<SampleServiceDto>>(
      sampleServiceEndpoints.create(),
      data
    );
    return response.data;
  }

  /** Update existing service */
  async update(id: ID, data: UpdateSampleServiceRequest): Promise<ApiResponse<SampleServiceDto>> {
    const response = await put<ApiResponse<SampleServiceDto>>(
      sampleServiceEndpoints.update(id),
      data
    );
    return response.data;
  }

  /** Soft-delete service */
  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(sampleServiceEndpoints.delete(id));
    return response.data;
  }

  /** Check whether a code is available (true = available) */
  async isCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const endpoint = sampleServiceEndpoints.validateCode(code);
    const url = excludeId
      ? buildEndpointWithQuery(endpoint, { excludeId })
      : endpoint;
    const response = await get<ApiResponse<boolean>>(url);
    return response.data.data;
  }
}

export const sampleServiceService = new SampleServiceService();
export default sampleServiceService;
